<template>
  <DashboardLayout>
    <template #sidebar>
      <DashboardSidebar
        :user-profile="sidebarUserProfile"
        :navigation-items="navigationItems"
        :active-item="activeItem"
        :logo-url="logoUrl"
        :organization-logo-url="organizationLogoUrl"
        :can-switch-roles="authStore.canSwitchRoles"
        :available-roles="authStore.availableRoles"
        :show-logo-placeholder="showLogoPlaceholder"
        :is-self-serve="authStore.isSelfServeProfile"
        :plan-type="userPlanType"
        @navigate="handleNavigation"
        @logout="handleLogout"
        @roleChange="handleRoleChange"
        @logoClick="handleLogoClick"
        @upgrade="handleUpgrade"
      />
    </template>

    <template v-if="secondarySidebarComponent" #secondarySidebar>
      <component :is="secondarySidebarComponent" />
    </template>

    <template #content>
      <router-view v-slot="{ Component, route }">
        <transition name="fade" mode="out-in">
          <div v-if="Component" :key="route.path" class="router-view-wrapper">
            <component :is="Component" />
          </div>
        </transition>
      </router-view>
    </template>

    <template v-if="rightSidebarComponent" #rightSidebar>
      <component :is="rightSidebarComponent" />
    </template>
  </DashboardLayout>
</template>

<script setup lang="ts">
import { computed, provide, ref, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { DashboardLayout, DashboardSidebar } from '@noovox/design-system'
import { getNavigationItems } from '../../config/navigation'
import { useAuthStore } from '../../stores/auth'
import { useUsersStore } from '../../stores/users'
import { useOrganizationStore } from '../../stores/organization'
import billingService from '../../services/billingService'
import type { PlanType } from '../../types/billing'

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const usersStore = useUsersStore()
const organizationStore = useOrganizationStore()

// Derive nav role from route path so admins viewing /profile/* see profile sidebar, not admin sidebar
const routeBasedRole = computed(() => {
  if (route.path.startsWith('/profile')) return 'profile' as const
  if (route.path.startsWith('/editor')) return 'editor' as const
  if (route.path.startsWith('/admin')) return 'admin' as const
  return authStore.userRole
})
const navigationItems = computed(() => getNavigationItems(routeBasedRole.value, authStore.organizationId))

// Override userProfile.role to match the route so the role badge label + color stay in sync
const sidebarUserProfile = computed(() => {
  const displayRole = routeBasedRole.value === 'profile' ? 'Profile'
    : routeBasedRole.value === 'editor' ? 'Editor'
    : 'Admin'
  return { ...authStore.userProfile, role: displayRole }
})

// User plan type for self-serve profiles (free, pro, or null for non-self-serve)
const userPlanType = ref<PlanType | null>(null)

// Main app logo URL (Noovox logo) - always use default
const logoUrl = computed(() => '/assets/noovox-icon.png')

// Organization logo URL (company logo for placeholder)
const organizationLogoUrl = computed(() => organizationStore.logoUrl)

// Show logo placeholder only for users connected to a business (not self-serve profiles)
const showLogoPlaceholder = computed(() => !!authStore.organizationId)

// Fetch organization data on mount (only for users with an organization)
// Also fetch billing info for self-serve profiles
onMounted(async () => {
  // For self-serve profiles, fetch subscription status to show upgrade/pro badge
  if (authStore.isSelfServeProfile) {
    try {
      const subscriptionStatus = await billingService.getSubscriptionStatus()
      userPlanType.value = subscriptionStatus.is_subscribed ? 'pro' : 'free'
    } catch (error) {
      console.error('[MainLayout] Failed to fetch subscription status:', error)
      userPlanType.value = 'free' // Default to free if fetch fails
    }
  }

  // Skip organization data fetch for self-serve profiles
  if (!authStore.organizationId) {
    return
  }
  try {
    await Promise.all([
      organizationStore.fetchOrganization(),
      organizationStore.fetchDepartments()
    ])
  } catch (error) {
    console.error('[MainLayout] Failed to fetch organization data:', error)
  }
})

// Sidebar components (can be set by child routes via provide/inject)
const secondarySidebarComponent = ref(null)
const rightSidebarComponent = ref(null)

// Provide function to set sidebar components
provide('setSecondarySidebar', (component: any) => {
  secondarySidebarComponent.value = component
})

provide('setRightSidebar', (component: any) => {
  rightSidebarComponent.value = component
})

// Clear sidebars on route change
watch(() => route.path, () => {
  secondarySidebarComponent.value = null
  rightSidebarComponent.value = null
})

// Store previous active navigation item to preserve it when navigating to create post routes
const previousActiveItem = ref<string>('dashboard')

// Helper function to compute active item from route
function computeActiveItemFromRoute(routeName: string | null | undefined, routePath: string): string {
  const routeNameStr = routeName as string

  // Match by route name (admin routes)
  if (routeNameStr === 'AdminDashboard' || routeNameStr === 'InviteUser') return 'dashboard'
  if (routeNameStr === 'AdminAnalytics') return 'analytics'
  if (routeNameStr === 'Scheduler') return 'scheduler'
  if (routeNameStr === 'TeamContent' || routeNameStr === 'NewPromotion') return 'content'
  if (routeNameStr === 'AdminSettings' || routeNameStr === 'OrgSettings' || routeNameStr === 'TeamSettings' || routeNameStr === 'AdminUserSettings' || routeNameStr === 'IndividualUserSettings') return 'settings'
  if (routeNameStr === 'ActivityLog') return 'activity-log'

  // Match by route name (editor routes - same as admin)
  if (routeNameStr === 'EditorDashboard') return 'dashboard'
  if (routeNameStr === 'EditorAnalytics') return 'analytics'
  if (routeNameStr === 'EditorScheduler') return 'scheduler'
  if (routeNameStr === 'EditorContent') return 'content'

  // Match by route name (profile routes - old editor design)
  if (routeNameStr === 'ProfileDashboard') return 'dashboard'
  if (routeNameStr === 'ProfileContent') return 'content'
  if (routeNameStr === 'ProfileAnalytics') return 'analytics'
  if (routeNameStr === 'ProfileScheduler') return 'scheduler'
  if (routeNameStr === 'ProfileCommenter') return 'commenter'
  if (routeNameStr === 'ProfileSettings') return 'settings'

  // Fallback: match by path segments
  if (routePath.includes('/admin/dashboard') || routePath.includes('/admin/team')) return 'dashboard'
  if (routePath.includes('/analytics')) return 'analytics'
  if (routePath.includes('/scheduler')) return 'scheduler'
  if (routePath.includes('/team-content')) return 'content'
  if (routePath.includes('/settings')) return 'settings'
  if (routePath.includes('/activity-log')) return 'activity-log'
  if (routePath.includes('/commenter')) return 'commenter'
  if (routePath.includes('/editor/dashboard')) return 'dashboard'
  if (routePath.includes('/profile/dashboard')) return 'dashboard'
  // Only match /content if it's not a create route
  if (routePath.includes('/content') && !routePath.includes('/content/create')) return 'content'

  return 'dashboard'
}

// Check if current route is a create post route
function isCreatePostRoute(routeName: string | null | undefined, routePath: string): boolean {
  const routeNameStr = routeName as string
  return routeNameStr === 'AdminCreatePost' ||
         routeNameStr === 'EditorCreatePost' ||
         routeNameStr === 'ProfileCreatePost' ||
         routePath.includes('/content/create')
}

// Active navigation item based on current route
const activeItem = computed(() => {
  const routeName = route.name as string
  const routePath = route.path

  // If on create post route, preserve the previous active item
  if (isCreatePostRoute(routeName, routePath)) {
    return previousActiveItem.value
  }

  return computeActiveItemFromRoute(routeName, routePath)
})

watch(
  () => [route.name, route.path] as const,
  ([routeName, routePath]) => {
    const name = routeName as string
    const path = routePath as string
    if (!isCreatePostRoute(name, path)) {
      previousActiveItem.value = computeActiveItemFromRoute(name, path)
    }
  },
  { immediate: true }
)

// Navigation handler
const handleNavigation = (item: any) => {
  // Use href from navigation item if available, otherwise construct path
  if (item.href) {
    router.push(item.href)
  } else if (item.id === 'activity-log') {
    router.push('/admin/activity-log')
  } else {
    router.push(`/admin/${item.id}`)
  }
}

// Logout handler
const handleLogout = async () => {
  await authStore.logout()
  router.push('/auth')
}

// Logo click handler - navigate to dashboard based on current user role
const handleLogoClick = () => {
  const currentRole = authStore.userRole
  let targetPath = '/admin/content' // Default fallback

  if (currentRole === 'profile') {
    targetPath = '/profile/dashboard'
  } else if (currentRole === 'editor') {
    targetPath = '/editor/dashboard'
  } else if (currentRole === 'admin') {
    targetPath = '/admin/content'
  }
  
  router.push(targetPath)
}

// Upgrade click handler - navigate to pricing page
const handleUpgrade = () => {
  router.push('/profile/pricing')
}

// Role change handler
const handleRoleChange = (role: string) => {
  console.log('[MainLayout] handleRoleChange called with role:', role)
  console.log('[MainLayout] Before change - isLoggedIn:', authStore.isLoggedIn, 'actualUserRole:', authStore.actualUserRole, 'userRole:', authStore.userRole, 'canSwitchRoles:', authStore.canSwitchRoles)

  // Admins and editors can switch roles (to profile view)
  if (!authStore.canSwitchRoles) {
    console.warn('[MainLayout] User cannot switch roles - canSwitchRoles is false')
    return
  }

  authStore.setCurrentRole(role)

  console.log('[MainLayout] After setCurrentRole - isLoggedIn:', authStore.isLoggedIn, 'actualUserRole:', authStore.actualUserRole, 'userRole:', authStore.userRole)

  // Navigate to the appropriate dashboard based on selected role
  const normalizedRole = role.toLowerCase()
  let targetPath = '/admin/content'
  if (normalizedRole === 'editor') {
    targetPath = '/editor/dashboard'
  } else if (normalizedRole === 'profile') {
    targetPath = '/profile/dashboard'
  }
  console.log('[MainLayout] Navigating to:', targetPath)

  router.push(targetPath)
}
</script>

<style scoped>
/* Layout wrapper styles are handled by DashboardLayout */

/* Smooth page transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
